import * as React from 'react';
import Box from '@mui/material/Box';
import { motion } from 'framer-motion';
import { staggerItem } from './motion/Reveal';

/**
 * Card with a cursor-following radial spotlight and a subtle 3D tilt.
 * Composes with <Stagger> via the staggerItem variants.
 */
export default function SpotlightCard({ children, sx, tilt = true }) {
  const ref = React.useRef(null);

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    el.style.setProperty('--mx', `${px * 100}%`);
    el.style.setProperty('--my', `${py * 100}%`);
    if (tilt) {
      const rx = (0.5 - py) * 8;
      const ry = (px - 0.5) * 8;
      el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
    }
  };
  const handleLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = 'perspective(900px) rotateX(0) rotateY(0) translateY(0)';
  };

  return (
    <motion.div variants={staggerItem} style={{ height: '100%' }}>
      <Box
        ref={ref}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        sx={{
          position: 'relative',
          height: '100%',
          borderRadius: 3,
          p: '1px',
          transition: 'transform 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s ease',
          transformStyle: 'preserve-3d',
          background: (t) =>
            t.palette.mode === 'dark'
              ? 'linear-gradient(180deg, hsla(220,25%,25%,0.35), hsla(220,25%,25%,0.12))'
              : 'linear-gradient(180deg, hsla(220,25%,80%,0.5), hsla(220,25%,90%,0.25))',
          '&:hover': {
            boxShadow: (t) =>
              t.palette.mode === 'dark'
                ? '0 20px 50px hsla(220,60%,10%,0.6)'
                : '0 20px 50px hsla(220,40%,60%,0.3)',
          },
          '&:hover .fx-spot': { opacity: 1 },
          '&:hover .fx-border': { opacity: 1 },
          ...sx,
        }}
      >
        {/* Gradient border glow on hover */}
        <Box
          className="fx-border"
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 3,
            padding: '1px',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            background:
              'linear-gradient(120deg, hsl(210,98%,55%), hsl(265,90%,62%), hsl(190,95%,55%))',
            WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'relative',
            height: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          {/* Spotlight */}
          <Box
            className="fx-spot"
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0,
              transition: 'opacity 0.3s ease',
              background:
                'radial-gradient(360px circle at var(--mx,50%) var(--my,50%), hsla(210,98%,60%,0.14), transparent 45%)',
              pointerEvents: 'none',
            }}
          />
          <Box sx={{ position: 'relative', height: '100%' }}>{children}</Box>
        </Box>
      </Box>
    </motion.div>
  );
}
